// Gestion Ultra-Agressive des erreurs ResizeObserver - Version 2025
class ResizeObserverErrorHandler {
  constructor() {
    this.originalError = console.error;
    this.originalWarn = console.warn;
    this.originalLog = console.log;
    this.isInitialized = false;
    
    this.init();
  }

  init() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // SUPPRESSION TOTALE ET DÉFINITIVE des erreurs ResizeObserver
    console.error = (...args) => {
      if (this.isResizeObserverError(args)) {
        return; // Supprime complètement - aucun log
      }
      this.originalError.call(console, ...args);
    };

    console.warn = (...args) => {
      if (this.isResizeObserverError(args)) {
        return; // Supprime complètement - aucun log
      }
      this.originalWarn.call(console, ...args);
    };

    console.log = (...args) => {
      if (this.isResizeObserverError(args)) {
        return; // Supprime complètement - aucun log
      }
      this.originalLog.call(console, ...args);
    };

    // Patch ULTRA-AGRESSIF pour capturer toutes les erreurs ResizeObserver
    const originalWindowError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      if (this.isResizeObserverError([message])) {
        return true; // Empêche la propagation
      }
      if (originalWindowError) {
        return originalWindowError.call(window, message, source, lineno, colno, error);
      }
      return false;
    };

    // Patch pour les erreurs non capturées (unhandledrejection)
    window.addEventListener('unhandledrejection', (event) => {
      if (this.isResizeObserverError([event.reason?.message || event.reason])) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    }, true);

    // Patch pour les erreurs générales
    window.addEventListener('error', (event) => {
      if (this.isResizeObserverError([event.error?.message || event.message])) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
    }, true);

    // PATCH DIRECT de ResizeObserver pour prévenir les erreurs à la source
    if (window.ResizeObserver) {
      const OriginalResizeObserver = window.ResizeObserver;
      
      window.ResizeObserver = class extends OriginalResizeObserver {
        constructor(callback) {
          const wrappedCallback = (entries, observer) => {
            try {
              // Déférer l'exécution pour éviter les boucles
              requestAnimationFrame(() => {
                try {
                  callback(entries, observer);
                } catch (error) {
                  // Supprimer silencieusement les erreurs ResizeObserver
                  if (!this.isResizeObserverError([error.message])) {
                    throw error;
                  }
                }
              });
            } catch (error) {
              // Supprimer silencieusement les erreurs ResizeObserver
              if (!this.isResizeObserverError([error.message])) {
                throw error;
              }
            }
          };
          
          super(wrappedCallback);
        }
        
        isResizeObserverError(args) {
          if (!args[0]) return false;
          const errorMessage = typeof args[0] === 'string' ? args[0] : args[0].toString();
          return errorMessage.toLowerCase().includes('resizeobserver');
        }
      };
    }
  }

  isResizeObserverError(args) {
    if (!args || !args[0]) return false;
    
    const errorMessage = typeof args[0] === 'string' ? args[0] : 
                        (args[0]?.message || args[0]?.toString() || '');
    
    const errorPatterns = [
      'resizeobserver loop completed with undelivered notifications',
      'resizeobserver loop limit exceeded', 
      'resizeobserver callback exceeded the timeout',
      'non-finite values in resizeobserver',
      'resizeobserver exceeded the maximum',
      'resize loop completed with undelivered notifications',
      'resizeobserver',
      'resize observer'
    ];
    
    return errorPatterns.some(pattern => 
      errorMessage.toLowerCase().includes(pattern)
    );
  }

  // Méthode pour restaurer le comportement original (pour debug uniquement)
  restore() {
    console.error = this.originalError;
    console.warn = this.originalWarn;
    console.log = this.originalLog;
    this.isInitialized = false;
  }
}

// Initialiser le gestionnaire d'erreurs ResizeObserver
const resizeObserverHandler = new ResizeObserverErrorHandler();

// Utilitaire pour créer des ResizeObserver sécurisés - Version renforcée
const createSafeResizeObserver = (callback, options = {}) => {
  // Timeout pour éviter les loops infinis
  const timeout = options.timeout || 16; // 16ms = ~60fps
  let timeoutId = null;
  let isProcessing = false;
  
  const safeCallback = (entries, observer) => {
    // Éviter les callbacks simultanés
    if (isProcessing) return;
    
    // Annuler le timeout précédent s'il existe
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    // Différer l'exécution pour éviter les loops
    timeoutId = setTimeout(() => {
      try {
        isProcessing = true;
        
        // Vérifier que les entrées sont valides
        if (!entries || !Array.isArray(entries) || entries.length === 0) {
          return;
        }
        
        // Filtrer et valider les entrées
        const validEntries = entries.filter(entry => {
          if (!entry || !entry.contentRect) return false;
          
          const { width, height } = entry.contentRect;
          return (
            isFinite(width) && 
            isFinite(height) && 
            width >= 0 && 
            height >= 0 &&
            width < 100000 && // Empêcher des tailles aberrantes
            height < 100000
          );
        });
        
        // Exécuter le callback seulement si on a des entrées valides
        if (validEntries.length > 0) {
          callback(validEntries, observer);
        }
        
      } catch (error) {
        // Suppression complète des erreurs ResizeObserver
        if (!error.message || !error.message.includes('ResizeObserver')) {
          if (process.env.NODE_ENV === 'development') {
            console.debug('[SafeResizeObserver] Callback error suppressed');
          }
        }
      } finally {
        isProcessing = false;
        timeoutId = null;
      }
    }, timeout);
  };

  try {
    const observer = new ResizeObserver(safeCallback);
    
    // Améliorer la méthode disconnect pour nettoyer les timeouts
    const originalDisconnect = observer.disconnect.bind(observer);
    observer.disconnect = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      isProcessing = false;
      originalDisconnect();
    };
    
    return observer;
  } catch (error) {
    // Fallback si ResizeObserver échoue
    return {
      observe: () => {},
      unobserve: () => {},
      disconnect: () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      }
    };
  }
};

export { createSafeResizeObserver, resizeObserverHandler };
